import React, { useReducer, useEffect, useRef } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  CircularProgress,
} from '@mui/material';
import InlineAlert from '../../../shared/components/InlineAlert';
import {
  ExpandMore as ExpandMoreIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { usePromptService } from '../services/promptService';
import MarkdownContent from '../../../shared/components/MarkdownContent';

interface PromptPanelState {
  expanded: boolean;
  content: string | null;
  status: 'idle' | 'loading' | 'success' | 'error';
  error: string | null;
}

type PromptPanelAction =
  | { type: 'EXPAND' }
  | { type: 'COLLAPSE' }
  | { type: 'LOAD_START' }
  | { type: 'LOAD_SUCCESS'; content: string }
  | { type: 'LOAD_ERROR'; error: string }
  | { type: 'RESET' };

const initialState: PromptPanelState = {
  expanded: false,
  content: null,
  status: 'idle',
  error: null,
};

function promptPanelReducer(
  state: PromptPanelState,
  action: PromptPanelAction
): PromptPanelState {
  switch (action.type) {
    case 'EXPAND':
      return { ...state, expanded: true };
    case 'COLLAPSE':
      return { ...state, expanded: false };
    case 'LOAD_START':
      return { ...state, status: 'loading', error: null };
    case 'LOAD_SUCCESS':
      return { ...state, status: 'success', content: action.content, error: null };
    case 'LOAD_ERROR':
      return { ...state, status: 'error', error: action.error };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

interface InvestigationPlaybook {
  id: string;
  version: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  owner?: any;
  title?: string;
  variables?: Record<string, string>;
}

interface PromptPanelProps {
  playbook: InvestigationPlaybook;
  customerId: string;
  investigationId: string;
  /** Controlled when supplied, so the procedure chip above the box can open the playbook. */
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
}

export const PromptPanel: React.FC<PromptPanelProps> = ({
  playbook,
  customerId,
  investigationId,
  expanded,
  onExpandedChange,
}) => {
  const [state, dispatch] = useReducer(promptPanelReducer, initialState);
  const promptService = usePromptService();
  const isOpen = expanded ?? state.expanded;

  // The playbook belongs to the investigation; reopening is the next reader's act, not a state
  // that should survive the switch. The ref is what makes this an investigation change rather
  // than "any render where a callback got a new identity".
  const lastInvestigation = useRef(investigationId);
  useEffect(() => {
    if (lastInvestigation.current === investigationId) return;
    lastInvestigation.current = investigationId;
    dispatch({ type: 'RESET' });
    onExpandedChange?.(false);
  }, [investigationId, onExpandedChange]);

  // Fetching is driven by the panel being open rather than by the click that opened it, so the
  // content arrives whoever opened it: the accordion header or the procedure chip above the box.
  // What the fetch must not re-run on: its own progress. Read through a ref so the dependency
  // list can name everything the effect reads without the fetch restarting itself.
  const fetchState = useRef(state);
  fetchState.current = state;
  useEffect(() => {
    if (!isOpen || fetchState.current.content || fetchState.current.status === 'loading') return;
    let cancelled = false;
    (async () => {
      try {
        dispatch({ type: 'LOAD_START' });
        const promptData = await promptService.getPromptVersion(
          playbook.id,
          playbook.version || '1',
          playbook.owner,
          playbook.owner === 'customer' ? customerId : undefined
        );
        if (!cancelled) dispatch({ type: 'LOAD_SUCCESS', content: promptData.content || '' });
      } catch (err) {
        console.error('Failed to load prompt content:', err);
        if (!cancelled) dispatch({ type: 'LOAD_ERROR', error: 'Failed to load prompt content' });
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, playbook.id, playbook.version, playbook.owner, customerId, promptService]);

  const handleChange = (_event: React.SyntheticEvent, next: boolean) => {
    dispatch({ type: next ? 'EXPAND' : 'COLLAPSE' });
    onExpandedChange?.(next);
  };

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 1,
        bgcolor: 'background.default',
      }}
    >
      <Accordion
        expanded={isOpen}
        onChange={handleChange}
        sx={{
          borderBottom: '1px solid',
          borderColor: 'divider',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            bgcolor: 'background.paper',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <MenuBookIcon fontSize="small" color="primary" />
            <Typography variant="body2" color="primary">
              Playbook: {playbook.title} (version {playbook.version})
            </Typography>
          </Box>
        </AccordionSummary>

        <AccordionDetails
          sx={{
            maxHeight: '60vh',
            overflow: 'auto',
            bgcolor: 'action.hover',
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 2,
          }}
        >
          {state.status === 'loading' && (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={24} />
              <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                Loading prompt content...
              </Typography>
            </Box>
          )}
          {state.error && (
            <InlineAlert severity="error" sx={{ mb: 2 }}>
              {state.error}
            </InlineAlert>
          )}
          {state.status === 'success' && state.content && (
            <MarkdownContent content={state.content} />
          )}
          {state.status === 'idle' && !state.content && (
            <Typography variant="body2" color="text.secondary">
              Click to load prompt content
            </Typography>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default PromptPanel;
